import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-prose>` says about itself.
 *
 * This piece is the words on a page, and it exists because nothing else in the library is
 * (CAOS-389). The library's ninety-seven other pieces are controls, containers, and record
 * surfaces: the text field collects a sentence, the inline hint explains one field, the notice
 * banner states a condition the surface is in, the detail section groups the fields of a record.
 * (Named in words, not as `<caos-…>` tags — see the note in `prose.ts`: the placement census reads
 * a sibling's tag in this comment as this component reaching it.)
 * None of them is "the paragraphs of this page", so a page whose content IS
 * the text — a documentation page, an about page, a landing section — had nothing to be built out
 * of. On an external page that is not a matter of taste: `ExternalPageSectionSchema` admits only
 * component placements, so words with no component to sit in cannot reach the document at all.
 *
 * Two things worth knowing before writing one.
 *
 * The `body` input is TEXT and is never parsed as markup. Paragraphs are separated by a blank
 * line. That is a deliberate refusal rather than a gap: this piece is drawn on anonymous public
 * pages from metadata an org admin authors, and interpreting that value as HTML would make a
 * documentation page an injection surface reachable with no credential.
 *
 * The slot wins over the `body` input, and that precedence is not arbitrary. The kernel's site
 * renderer emits every string input twice — as a `data-` attribute so the element can upgrade, and
 * as a light-DOM paragraph so the page is legible before its bundle arrives — so on a public page
 * the words are already in the children. Reading the attribute as well would print the page twice.
 */
const docs: ComponentDocs = {
  aliases: ['rich text', 'body copy', 'paragraph', 'text block', 'content block', 'markdown block', 'long form', 'copy'],

  answers: [
    'put the words of a documentation page on the page',
    'write an about page or a landing section',
    'explain a screen in a paragraph rather than a tooltip',
    'put a section of narrative between two record surfaces',
    'author page content that an admin can edit without a release',
  ],

  // ── WHAT THIS LIST DOES AND DOES NOT GATE ──────────────────────────────────────────────────
  // CAOS-709 holds a placed component to the placements it declares BY THE SURFACE KIND of the
  // page's blueprint — and that check reads a kind only for a `page`. `deploy/validate.ts` resolves
  // `pageKind` under `nc.type === 'page'` and its own comment says the rest "arrive here with
  // nothing to name": a roster page, a record action, and A SITE PAGE. So an `externalPage` — the
  // documentation surface this component was built for — is NOT judged by kind. The only placement
  // rule that reaches it is the weaker one (CAOS-1814): a piece declaring nowhere, or declaring
  // only `seam`, can never be placed on any surface that places things.
  //
  // Recorded that way round deliberately. An earlier draft of this comment claimed the external
  // page was judged as its template's kind, which reads as a reason this list has to contain
  // `homePage` — and it does not. The list is what governs the INTERNAL surfaces below, and those
  // are real: prose above a record's fields, a paragraph on an app or home page, the opening of a
  // step in a guided flow. Believing it gated documentation pages would send the next person
  // widening it for a check that never runs.
  placements: ['homePage', 'appPage', 'recordPage', 'screen'],

  inputs: [
    {
      name: 'heading',
      attribute: 'heading',
      type: 'text',
      description:
        'An optional heading above the prose. Unset, no heading element is drawn at all — rather ' +
        'than an empty one, which would still be a heading in the document outline and would read ' +
        'as a blank entry to a screen reader.',
    },
    {
      name: 'level',
      attribute: 'level',
      type: 'text',
      options: ['h2', 'h3', 'h4'],
      description:
        'Which level the heading is. `h3` by default, because both renderers already put a heading ' +
        'above this one: the site renderer emits the page label as an h1 and each section name as ' +
        'an h2, and an internal surface draws a page header. Taking `h2` here would put two h2s at ' +
        'different depths in one document, which reads as two sibling sections rather than a ' +
        'section and the block inside it.',
      default: 'h3',
    },
    {
      name: 'body',
      attribute: 'body',
      type: 'text',
      description:
        'The prose. Paragraphs are separated by a blank line and each one is set as TEXT — no ' +
        'markup is interpreted, because this piece is drawn on anonymous public pages from ' +
        'authored metadata. Ignored when anything has been slotted in, since the kernel\'s site ' +
        'renderer supplies the same words as light-DOM children and rendering both would print ' +
        'them twice.',
    },
  ],

  events: [],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The paragraphs, when they are supplied as content rather than through the `body` input. ' +
        'This is the path a public page takes: the site renderer writes the words into the ' +
        'document as paragraphs so the page reads with JavaScript off, and this piece styles them ' +
        'in place rather than redrawing them. That renderer echoes EVERY input as a paragraph, not ' +
        'only the prose, so a paragraph naming an input other than `body` is hidden once this ' +
        'piece upgrades — the heading is already drawn as a heading, and the level as its tag.',
    },
  ],

  tokens: [
    { token: '--caos-measure-document', controls: 'The reading width. The point of the piece: a documentation paragraph run full-width across a wide page is what makes long-form text unreadable.' },
    { token: '--caos-font', controls: 'The typeface the prose is set in.' },
    { token: '--caos-text-body', controls: 'The size of the prose, and the size the heading is scaled from.' },
    { token: '--caos-font-weight-body', controls: 'The weight of the prose.' },
    { token: '--caos-font-display', controls: 'The typeface of the heading.' },
    { token: '--caos-font-weight-display', controls: 'The weight of the heading.' },
    { token: '--caos-color-text', controls: 'The ink of both the heading and the prose.' },
    { token: '--caos-space-3', controls: 'The gap between one paragraph and the next.' },
    { token: '--caos-space-2', controls: 'The gap under the heading.' },
    { token: '--caos-color-accent', controls: 'A link inside the prose. The accent rather than a link token of its own, because the platform scale has none and a component may not read a name the design system does not define.' },
  ],

  parts: [
    { part: 'prose', is: 'the block as a whole, including its reading measure' },
    { part: 'heading', is: 'the heading line' },
    { part: 'body', is: 'the paragraphs' },
  ],

  methods: [],

  placing:
    'In its own section, at the width the region gives it — the piece caps its own reading measure, ' +
    'so it does not need a narrow column built around it. On a documentation page it is the content ' +
    'and there is usually one per section, under that section\'s heading. On a record page it goes ' +
    'above the fields it explains, not below them, for the same reason a notice does: a paragraph ' +
    'placed after the thing it introduces has already been skipped.',

  whenToUse:
    'The content of the surface IS the text — a documentation page, an about page, a landing ' +
    'section, or a standing explanation above a record\'s fields. Reach for it whenever words are ' +
    'the deliverable rather than the label on something else.',

  insteadUse: [
    {
      name: 'InlineHint',
      when:
        'The note explains ONE field and belongs under it, in muted text. That piece is sized and ' +
        'coloured to sit beneath a control without competing with it; a prose block there reads as ' +
        'page content that has fallen into the form.',
    },
    {
      name: 'NoticeBanner',
      when:
        'The text is a CONDITION the surface is in and wants a decision or must not be missed. A ' +
        'banner is tinted, full-width and dismissable for that reason. Prose is content; a banner ' +
        'qualifies content.',
    },
    {
      name: 'DetailSection',
      when:
        'The block is the fields of a record grouped under a title, not sentences. That piece ' +
        'draws the group; this one would only describe it.',
    },
    {
      name: 'SummaryPanel',
      when:
        'The words are GENERATED from a record by the Summarize capability and their claims carry ' +
        'citations back into it. That panel owns the citation behaviour; prose is authored text ' +
        'with nothing to cite.',
    },
    {
      name: 'CodeEditor',
      when:
        'The content is a code sample a reader may edit or run. Prose sets sentences at a reading ' +
        'measure in the body typeface, which is the wrong shape for code and loses its whitespace.',
    },
  ],

  accessibility:
    'The heading is a real heading element and its LEVEL is the `level` input, because a heading\'s ' +
    'tag is its level to assistive technology and cannot be faked with styling — which is why ' +
    'this piece retags the element rather than restyling one fixed tag. A block with no heading ' +
    'draws no heading element, so the document outline never carries a blank entry. The prose is ' +
    'ordinary paragraphs either way, so it is navigable by paragraph and selectable as text. Left ' +
    'to the author: choosing a level that follows the heading above it rather than the size that ' +
    'looks right, and keeping the words in the `body` input or the slot rather than folding them ' +
    'into the heading.',

  examples: [
    {
      key: 'documentation-page',
      label: 'A documentation page',
      summary:
        'The case the piece was built for: a heading and two paragraphs that ARE the page, set to a ' +
        'reading measure rather than the width of the window.',
      placement: {
        component: 'caos-prose',
        inputs: {
          heading: 'What a metatype is',
          body:
            'A metatype is the platform\'s own description of a kind of metadata. An object, a field, a page and a permission set are each a metatype, and each one is a schema the platform validates every deploy against.\n\nThat is why a change to the platform cannot quietly widen what an org may author: the schema is the contract, and a deploy that does not satisfy it is refused rather than accepted and worked around.',
        },
      },
    },
    {
      key: 'no-heading',
      label: 'Prose with no heading',
      summary:
        'A block that continues under a heading something else already drew — a section name, or the ' +
        'page title. No heading element is drawn at all, so the outline stays clean.',
      placement: {
        component: 'caos-prose',
        inputs: {
          body:
            'Every org gets a primary hostname when it is provisioned, in the same transaction as the org itself, because an org committed without one is unroutable whatever DNS says.',
        },
      },
    },
    {
      key: 'levels',
      label: 'The three heading levels',
      summary:
        'h2, h3 and h4 side by side. The default is h3, which is the level a block inside a section ' +
        'actually sits at — pick the one that follows the heading above it, not the size that looks right.',
      placement: [
        { component: 'caos-prose', inputs: { level: 'h2', heading: 'A section of a page', body: 'Set the level from the document, not from the size you want.' } },
        { component: 'caos-prose', inputs: { level: 'h3', heading: 'A block inside that section', body: 'This is the default, and the one most blocks want.' } },
        { component: 'caos-prose', inputs: { level: 'h4', heading: 'A point inside that block', body: 'Below this, prose with no heading is usually the better shape.' } },
      ],
    },
    {
      key: 'slotted',
      label: 'Words supplied as content',
      summary:
        'The path a public page takes. The site renderer writes the paragraphs into the document ' +
        'itself so the page reads before its bundle arrives; the piece styles them where they are ' +
        'and ignores the `body` input, which carries the same words.',
      placement: {
        component: 'caos-prose',
        inputs: { heading: 'Served before the bundle arrives' },
        children: [
          'A public page is rendered on the server and cached at the edge, so the words have to be in the markup rather than drawn by script.',
        ],
      },
    },
  ],
};

export default docs;
